import { z } from "zod";

const COLLABO_LINK_ERROR_CODES = [
  "COLLABO_PENDING_EXPIRED",
  "COLLABO_PENDING_CHANGED",
  "INVALID_CREDENTIALS",
  "LINK_CONFLICT",
  "LINK_IN_PROGRESS",
  "SESSION_ISSUE_FAILED",
] as const;

export type CollaboLinkErrorCode =
  | (typeof COLLABO_LINK_ERROR_CODES)[number]
  | "UNKNOWN";

const LinkResponseSchema = z.object({ linked: z.boolean() });

const ErrorResponseSchema = z.object({
  code: z.enum(COLLABO_LINK_ERROR_CODES),
});

export type CollaboLinkResult =
  | { kind: "linked" }
  | { kind: "link_failed" }
  // The request never reached the BE or no response came back (offline, DNS, aborted). Unlike
  // an "error" there is no status or code, and the BE may or may not have processed the call.
  | { kind: "network_error" }
  | { kind: "error"; status: number; code: CollaboLinkErrorCode };

// LINK_IN_PROGRESS means an earlier /link of this tab (e.g. one whose answer was lost while
// offline) still holds the Collabo register call. The BE frees the row at the latest after the
// register budget plus its lock margin (~49s), so the resends span a little longer than that.
export const LINK_IN_PROGRESS_RETRY = {
  delayMs: 3000,
  maxRetries: 17,
} as const;

// Same origin as the SPA: the web host forwards /v3/collabo/* to the API (CloudFront when
// deployed, a dev rewrite in next.config.mjs locally), so the flow's cookies stay host-only.
const LINK_URL = "/v3/collabo/portal/link";
const PENDING_URL = "/v3/collabo/portal/pending";

// Amplify signIn establishes the Cognito session; the id_token proves it to the BE. The
// pending token still travels only in the HttpOnly __Host-collabo_pending cookie. The BE
// answers 200 JSON (not 303) so this fetch can read the body and let the SPA navigate. /link
// is confirm-only: it issues no session cookie — the SPA enters the app on its own signIn
// session. The pending cookie goes with the request and, on linked=false, the response
// stores the collabo_link_failed cookie.
//
// linkRef is the fingerprint of the Collabo ID this tab displayed (from GET /pending). The
// pending cookie is shared by every tab, so the BE compares it with the cookie's current
// Collabo ID and refuses (COLLABO_PENDING_CHANGED) when another tab replaced it.
export const postCollaboPortalLink = async (
  idToken: string,
  linkRef: string,
  isTermsOfUseAgreed = false,
): Promise<CollaboLinkResult> => {
  const response = await fetch(LINK_URL, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      is_terms_of_use_agreed: isTermsOfUseAgreed,
      link_ref: linkRef,
    }),
  }).catch(() => null);

  if (response === null) {
    return { kind: "network_error" };
  }

  const body = await response.json().catch(() => null);

  if (response.ok) {
    const parsed = LinkResponseSchema.safeParse(body);
    // A 200 without a readable {linked} must never be treated as success.
    if (!parsed.success) {
      return { kind: "error", status: response.status, code: "UNKNOWN" };
    }
    return parsed.data.linked ? { kind: "linked" } : { kind: "link_failed" };
  }

  const parsed = ErrorResponseSchema.safeParse(body);
  return {
    kind: "error",
    status: response.status,
    code: parsed.success ? parsed.data.code : "UNKNOWN",
  };
};

export type CollaboPortalPendingInfo = {
  name: string | null;
  email: string | null;
};

export type CollaboPortalPending = {
  // null when the pending is gone or could not be read.
  info: CollaboPortalPendingInfo | null;
  // Identifies the Collabo ID being linked; null whenever there is no readable pending.
  linkRef: string | null;
};

const NO_PENDING: CollaboPortalPending = { info: null, linkRef: null };

const PendingResponseSchema = z.object({
  collabo_name: z.string().nullish(),
  collabo_email: z.string().nullish(),
  link_ref: z.string(),
});

export const PENDING_INFO_TIMEOUT_MS = 5000;

// A dropped connection or a 5xx is retried once. A timeout is not, since the user already
// waited PENDING_INFO_TIMEOUT_MS behind the spinner.
const fetchCollaboPortalPending = async (): Promise<
  CollaboPortalPending | "retryable"
> => {
  // The sign-in form stays hidden until this settles, and the data is only a display
  // aid, so a stalled connection (e.g. a hospital proxy) must not block sign-in.
  // AbortController + setTimeout instead of AbortSignal.timeout, which needs Safari 16+.
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    PENDING_INFO_TIMEOUT_MS,
  );
  const response = await fetch(PENDING_URL, {
    method: "GET",
    credentials: "include",
    // A 410 is heuristically cacheable and older BE builds sent it without Cache-Control, so a
    // cached 410 would otherwise hide the new pending set by a restarted OIDC.
    cache: "no-store",
    signal: controller.signal,
  })
    .catch(() => null)
    .finally(() => clearTimeout(timeoutId));

  // No pending (cookie missing/expired → 410), a server error, a network failure, or a
  // timeout: the screen just renders without a name/email pre-fill. The user can still
  // type their email manually, and the real pending check happens again on POST /link.
  if (response === null) {
    return controller.signal.aborted ? NO_PENDING : "retryable";
  }
  if (response.status === 410) {
    return NO_PENDING;
  }
  if (response.status >= 500) {
    return "retryable";
  }
  if (!response.ok) {
    return NO_PENDING;
  }
  const body = await response.json().catch(() => null);
  const parsed = PendingResponseSchema.safeParse(body);
  if (!parsed.success) {
    return NO_PENDING;
  }
  return {
    info: {
      name: parsed.data.collabo_name ?? null,
      email: parsed.data.collabo_email ?? null,
    },
    linkRef: parsed.data.link_ref,
  };
};

// Display name / pre-fill email for the link-mode sign-in screen. Kept out of the OIDC
// callback's redirect URL: Datadog RUM and PostHog capture page-view URLs, which would leak
// this PII to those external SaaS tools. Authenticated by the HttpOnly
// __Host-collabo_pending cookie — no token, no JWT.
export const getCollaboPortalPending =
  async (): Promise<CollaboPortalPending> => {
    const first = await fetchCollaboPortalPending();
    if (first !== "retryable") {
      return first;
    }
    const second = await fetchCollaboPortalPending();
    return second === "retryable" ? NO_PENDING : second;
  };
